<script>
  import CountrySelect from '../components/CountrySelect.svelte';
  import { apiFetch } from '../lib/auth.js';

  // ── Records (passed in / bound by parent) ──────────────────────────
  export let records = [];   // array of artist objects, kept in sync via events

  // ── Form state ─────────────────────────────────────────────────────
  let showForm    = false;
  let editing     = null;
  let formError   = '';
  let formSuccess = false;
  let formLoad    = false;

  let form = blankForm();

  function blankForm() {
    return { name: '', formed_year: '', description: '', band_logo_url: '', country: '', status: '', genres: '' };
  }

  function openAdd() {
    form = blankForm();
    editing = null; showForm = true; formError = ''; formSuccess = false;
  }

  function openEdit(record) {
    form = {
      name:          record.name          || '',
      formed_year:   record.formed_year   != null ? String(record.formed_year) : '',
      description:   record.description   || '',
      band_logo_url: record.band_logo_url || '',
      country:       record.country       || '',
      status:        record.status        || '',
      genres:        Array.isArray(record.genres) ? record.genres.join(', ') : (record.genres || ''),
    };
    editing = record; showForm = true; formError = ''; formSuccess = false;
    setTimeout(() => document.querySelector('.form-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function closeForm() {
    showForm = false; editing = null; formError = ''; formSuccess = false;
  }

  async function submitForm() {
    if (!form.name.trim()) { formError = 'Name is required'; return; }
    formError = ''; formLoad = true;
    const isEdit = !!editing;
    const endpoint = isEdit ? `/artists/${editing.id}` : '/artists';
    const body = { name: form.name.trim() };
    if (form.formed_year)   body.formed_year   = Number(form.formed_year);
    if (form.description)   body.description   = form.description.trim();
    if (form.band_logo_url) body.band_logo_url = form.band_logo_url.trim();
    if (form.country)       body.country       = form.country.trim();
    if (form.status)        body.status        = form.status.trim();
    body.genres = form.genres
      ? form.genres.split(',').map(g => g.trim()).filter(Boolean)
      : [];
    try {
      const result = await apiFetch(endpoint, { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(body) });
      records = isEdit
        ? records.map(r => r.id === result.id ? result : r)
        : [result, ...records];
      formSuccess = true;
      setTimeout(closeForm, 900);
    } catch (e) {
      formError = e.message || 'Failed to save';
    } finally {
      formLoad = false;
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────
  let deleteLoad  = false;
  let deleteError = '';

  async function deleteRecord() {
    if (!editing) return;
    deleteError = '';
    deleteLoad  = true;
    try {
      await apiFetch(`/artists/${editing.id}`, { method: 'DELETE' });
      records = records.filter(r => r.id !== editing.id);
      closeForm();
    } catch (e) {
      deleteError = e.message || 'Failed to delete — please try again.';
    } finally {
      deleteLoad = false;
    }
  }

  // ── Dedupe / merge ─────────────────────────────────────────────────
  let dedupeOpen      = false;
  let canonicalIds    = {};
  let mergeGroupIdx   = null;
  let mergeGroupLoad  = false;
  let mergeGroupError = '';

  function getDupeGroups(list) {
    const groups = {};
    for (const r of list) {
      const key = r.name.toLowerCase().trim();
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    }
    return Object.values(groups).filter(g => g.length > 1);
  }

  $: dupeGroups = getDupeGroups(records);

  $: duplicateNames = (() => {
    const seen = {}; const dupes = new Set();
    for (const r of records) {
      const key = r.name.toLowerCase().trim();
      if (seen[key]) dupes.add(key); else seen[key] = true;
    }
    return dupes;
  })();

  function isDuplicate(r) {
    return duplicateNames.has(r.name.toLowerCase().trim());
  }

  function initCanonicals() {
    canonicalIds = {};
    dupeGroups.forEach((group, i) => {
      const scored = group.map(r => ({
        id: r.id,
        score: (r.country ? 1 : 0) + (r.formed_year ? 1 : 0) + (r.description ? 1 : 0),
      }));
      scored.sort((a, b) => b.score - a.score);
      canonicalIds[i] = scored[0].id;
    });
  }

  function openDedupe() {
    dedupeOpen = true;
    initCanonicals();
    mergeGroupError = '';
  }

  function closeDedupe() { dedupeOpen = false; canonicalIds = {}; }

  async function mergeGroup(groupIdx) {
    const group     = dupeGroups[groupIdx];
    const canonical = canonicalIds[groupIdx];
    if (!canonical) return;
    const duplicates = group.filter(r => r.id !== canonical);
    mergeGroupIdx = groupIdx; mergeGroupLoad = true; mergeGroupError = '';
    try {
      for (const dup of duplicates) {
        await apiFetch(`/artists/${dup.id}/merge`, {
          method: 'POST',
          body: JSON.stringify({ into_id: canonical }),
        });
        records = records.filter(r => r.id !== dup.id);
      }
      initCanonicals();
    } catch (e) {
      mergeGroupError = `Group ${groupIdx + 1}: ${e.message || 'Merge failed'}`;
    } finally {
      mergeGroupLoad = false; mergeGroupIdx = null;
    }
  }
</script>

<!-- ── List header ──────────────────────────────────────────────────── -->
{#if !showForm && !dedupeOpen}
  <div class="list-header">
    <span class="record-count">
      {records.length} records
      {#if duplicateNames.size > 0}
        <button class="dupe-badge-btn" on:click={openDedupe}>
          {dupeGroups.length} duplicate group{dupeGroups.length > 1 ? 's' : ''}
        </button>
      {/if}
    </span>
    <div class="list-header-actions">
      <button class="btn-add-top" on:click={openAdd}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Add Artist
      </button>
    </div>
  </div>
{/if}

<!-- ── Add / Edit form ──────────────────────────────────────────────── -->
{#if showForm}
  <div class="form-panel">
    <div class="form-header">
      <span>{editing ? 'Edit Artist' : 'Add Artist'}</span>
      <button class="btn-dismiss" on:click={closeForm}>✕</button>
    </div>

    {#if formSuccess}
      <div class="form-success">✓ {editing ? 'UPDATED' : 'ADDED'}</div>
    {:else}
      {#if formError}<div class="form-error">{formError}</div>{/if}

      <div class="field">
        <label for="artist-name">Name *</label>
        <input id="artist-name" type="text" bind:value={form.name} placeholder="e.g. Salmonsugarburg or whatever their name is" />
      </div>
      <div class="row">
        <div class="field">
          <label for="artist-formed-year">Formed Year</label>
          <input id="artist-formed-year" type="number" bind:value={form.formed_year} placeholder="e.g. 2019" min="1940" max="2999" />
        </div>
        <CountrySelect bind:value={form.country} />
      </div>
      <div class="field">
        <label for="artist-status">Status</label>
        <select id="artist-status" bind:value={form.status}>
          <option value="">Unknown</option>
          <option value="active">Active</option>
          <option value="disbanded">Disbanded</option>
          <option value="hiatus">Hiatus</option>
        </select>
      </div>
      <div class="field">
        <label for="artist-genres">Genres</label>
        <input id="artist-genres" type="text" bind:value={form.genres} placeholder="e.g. Black Metal, Death Metal" />
        <span class="field-hint">Comma-separated</span>
      </div>
      <div class="field">
        <label for="artist-desc">Description</label>
        <textarea id="artist-desc" bind:value={form.description} placeholder="Bio or notes..." rows="4" maxlength="5000"></textarea>
      </div>
      <div class="field">
        <label for="artist-logo">Band Logo URL</label>
        <input id="artist-logo" type="url" bind:value={form.band_logo_url} placeholder="https://..." />
      </div>

      <div class="btn-row">
        <button class="btn-secondary" on:click={closeForm}>Cancel</button>
        {#if editing}
          <button class="btn-danger" on:click={deleteRecord} disabled={deleteLoad}>
            {deleteLoad ? 'DELETING...' : 'DELETE'}
          </button>
        {/if}
        <button class="btn-primary" on:click={submitForm} disabled={formLoad}>
          {formLoad ? 'SAVING...' : editing ? 'SAVE CHANGES' : 'ADD ARTIST'}
        </button>
      </div>
      {#if deleteError}<div class="form-error">{deleteError}</div>{/if}
    {/if}
  </div>
{/if}

<!-- ── Dedupe view ───────────────────────────────────────────────────── -->
{#if dedupeOpen}
  <div class="dedupe-view">
    <div class="dedupe-header">
      <div class="dedupe-title">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M8 7h12M8 12h8M8 17h4"/><polyline points="16 3 21 8 16 13"/>
        </svg>
        Resolve duplicates — {dupeGroups.length} group{dupeGroups.length > 1 ? 's' : ''}
      </div>
      <button class="btn-dismiss" on:click={closeDedupe}>✕ Back to list</button>
    </div>

    {#if mergeGroupError}
      <div class="form-error">{mergeGroupError}</div>
    {/if}

    {#if dupeGroups.length === 0}
      <div class="state-msg empty">No duplicates — all clean.</div>
    {:else}
      {#each dupeGroups as group, i}
        <div class="dupe-group">
          <div class="dupe-group-header">
            <span class="dupe-group-name">"{group[0].name}"</span>
            <span class="dupe-group-count">{group.length} entries</span>
          </div>
          <div class="dupe-group-hint">Select the canonical entry to keep. Others will be merged into it.</div>
          <div class="dupe-candidates">
            {#each group as r}
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <div
                class="dupe-candidate"
                class:selected={canonicalIds[i] === r.id}
                role="radio"
                aria-checked={canonicalIds[i] === r.id}
                tabindex="0"
                on:click={() => canonicalIds[i] = r.id}
                on:keydown={e => e.key === 'Enter' && (canonicalIds[i] = r.id)}
              >
                <div class="dupe-radio">
                  {#if canonicalIds[i] === r.id}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4" fill="currentColor"/>
                    </svg>
                  {:else}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                  {/if}
                </div>
                <div class="dupe-details">
                  <span class="dupe-id">#{r.id}</span>
                  {#if r.formed_year}<span class="dupe-pill">{r.formed_year}</span>{/if}
                  {#if r.country}<span class="dupe-pill">{r.country}</span>{/if}
                  {#if r.status}<span class="dupe-pill">{r.status}</span>{/if}
                  {#if r.description}<span class="dupe-pill has-data" title={r.description}>bio ✓</span>{/if}
                  {#if !r.formed_year && !r.country && !r.description}
                    <span class="dupe-empty">no details</span>
                  {/if}
                </div>
                {#if canonicalIds[i] === r.id}
                  <span class="dupe-keep-label">KEEP</span>
                {:else}
                  <span class="dupe-absorb-label">ABSORB</span>
                {/if}
              </div>
            {/each}
          </div>
          <div class="dupe-group-actions">
            <button
              class="btn-merge-group"
              disabled={mergeGroupLoad && mergeGroupIdx === i}
              on:click={() => mergeGroup(i)}
            >
              {mergeGroupLoad && mergeGroupIdx === i ? 'Merging...' : `Merge ${group.length - 1} into #${canonicalIds[i]}`}
            </button>
          </div>
        </div>
      {/each}
    {/if}
  </div>

<!-- ── Record list ───────────────────────────────────────────────────── -->
{:else if !showForm}
  {#if records.length === 0}
    <div class="state-msg empty">No artists yet.</div>
  {:else}
    <div class="list">
      {#each records as record (record.id)}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div
          class="list-row"
          class:dupe-row={isDuplicate(record)}
          role="button" tabindex="0"
          on:click={() => openEdit(record)}
          on:keydown={e => e.key === 'Enter' && openEdit(record)}
        >
          <span class="row-label">
            {record.name}
            {#if record.country}<span class="row-country">({record.country})</span>{/if}
          </span>
          {#if isDuplicate(record)}
            <span class="dupe-indicator" title="Name conflict">≡</span>
          {/if}
          {#if record.formed_year}<span class="row-sub">{record.formed_year}</span>{/if}
          <svg class="row-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>
      {/each}
    </div>
  {/if}
{/if}
